// ─────────────────────────────────────────────────────────────
// AnalyticsService — page_analytics 테이블 책임 격리 (v7.89)
//
// 트래킹 원칙:
//   1. Fire-and-forget: 트래킹 실패는 앱 동작에 영향 없음
//   2. 세션 지속: localStorage로 브라우저 단위 익명 세션 유지
//   3. Dedupe: 짧은 시간 내 같은 페이지 뷰는 무시 (5초)
//
// 이벤트 유형:
//   - page_view: 페이지/서브탭 진입
//   - action: 배송 커밋, 입고 저장, 시리얼 스캔 등 주요 액션
//
// 스키마 참조: v7.89-schema-page-analytics.sql
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

const SESSION_KEY = 'inventura_session_id';
let lastPageView = { name: null, at: 0 };

const getOrCreateSessionId = () => {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // localStorage 접근 불가 (private mode 등) — 세션당 랜덤 발급
    return `s_ephemeral_${Date.now()}`;
  }
};

export const AnalyticsService = {
  getSessionId: getOrCreateSessionId,

  // 저수준 트래킹 — 실패는 silent (async 예외 catch)
  //   ⚠ 주의: 호출부에서 인자 평가 시 발생하는 sync 예외(예: 미정의 변수 참조)는
  //           함수 내부 try/catch로 잡을 수 없음. 호출부에서 유효한 인자만 넘길 것.
  async track(eventType, eventName, { staffName = null, meta = null } = {}) {
    try {
      await supabase.from('page_analytics').insert({
        event_type: eventType,
        event_name: eventName,
        session_id: getOrCreateSessionId(),
        staff_name: staffName || null,
        meta: meta || null,
      });
    } catch (e) {
      // 트래킹 실패는 무시 — 앱 동작에 영향 주면 안 됨
    }
  },

  // 페이지 뷰 (5초 dedupe로 노이즈 감소)
  async trackPageView(pageName) {
    const now = Date.now();
    if (lastPageView.name === pageName && now - lastPageView.at < 5000) return;
    lastPageView = { name: pageName, at: now };
    return this.track('page_view', pageName);
  },

  // 주요 액션 (담당자 이름 함께)
  async trackAction(actionName, staffName, meta) {
    return this.track('action', actionName, { staffName, meta });
  },

  // ─── 통계 조회 ───

  // 기간별 전체 이벤트 조회 (대시보드 원본)
  async loadEvents({ fromISO, toISO, limit = 10000 } = {}) {
    let q = supabase.from('page_analytics').select('*');
    if (fromISO) q = q.gte('created_at', fromISO);
    if (toISO)   q = q.lte('created_at', toISO);
    const { data, error } = await q.order('created_at', { ascending: false }).limit(limit);
    if (error) return [];
    return data || [];
  },

  // 요약 통계 (client-side 집계)
  //   - totalViews / totalActions
  //   - uniqueSessions
  //   - pageRank: [{name, count}, ...]
  //   - actionRank: [{name, count}, ...]
  //   - staffRank: [{name, count}, ...]  (action 기준)
  //   - hourlyBuckets: 24개 시간대별 총 이벤트 수
  //   - dailyBuckets: 최근 N일 일별 이벤트 수
  summarize(events, { days = 30 } = {}) {
    const summary = {
      totalViews: 0,
      totalActions: 0,
      uniqueSessions: new Set(),
      pageRank: {},
      actionRank: {},
      staffRank: {},
      hourlyBuckets: Array(24).fill(0),
      dailyBuckets: {},
    };

    const now = new Date();
    for (let i = 0; i < days; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      summary.dailyBuckets[key] = 0;
    }

    for (const e of events) {
      summary.uniqueSessions.add(e.session_id);
      const dt = new Date(e.created_at);
      const hour = dt.getHours();
      summary.hourlyBuckets[hour]++;
      const dayKey = dt.toISOString().slice(0, 10);
      if (dayKey in summary.dailyBuckets) summary.dailyBuckets[dayKey]++;

      if (e.event_type === 'page_view') {
        summary.totalViews++;
        summary.pageRank[e.event_name] = (summary.pageRank[e.event_name] || 0) + 1;
      } else if (e.event_type === 'action') {
        summary.totalActions++;
        summary.actionRank[e.event_name] = (summary.actionRank[e.event_name] || 0) + 1;
        if (e.staff_name) {
          summary.staffRank[e.staff_name] = (summary.staffRank[e.staff_name] || 0) + 1;
        }
      }
    }

    // 정렬된 배열로 변환
    const toRank = (obj) => Object.entries(obj)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalViews: summary.totalViews,
      totalActions: summary.totalActions,
      uniqueSessions: summary.uniqueSessions.size,
      pageRank: toRank(summary.pageRank),
      actionRank: toRank(summary.actionRank),
      staffRank: toRank(summary.staffRank),
      hourlyBuckets: summary.hourlyBuckets,
      dailyBuckets: Object.entries(summary.dailyBuckets)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({ date, count })),
    };
  },
};
